import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { courseTypeaheadRows, type CourseSearchRow } from '@/lib/recentCourseSelections';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export type CourseTemplateChoice = CourseSearchRow & {
  detail: string;
};

/**
 * Vælg forløb. Same search as the patient field and Aftaletype on Ny aftale:
 * focus opens a list under the field, typing filters it, and one row is the
 * choice. Recent picks and the other bookable forløb each sit in a labelled
 * group with a light highlight. Nothing is selected until the user clicks.
 */
export function CourseTemplateTypeahead({
  courses,
  value,
  recentIds,
  onChange,
  label,
  placeholder,
  changeLabel,
  emptyLabel,
  recentLabel,
  availableLabel,
}: {
  courses: readonly CourseTemplateChoice[];
  value: string | null;
  recentIds: readonly string[];
  onChange: (id: string | null) => void;
  label: string;
  placeholder: string;
  changeLabel: string;
  emptyLabel: string;
  recentLabel: string;
  availableLabel: string;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = courses.find((course) => course.id === value) ?? null;
  const { favorites, available } = useMemo(
    () => courseTypeaheadRows(courses, search, recentIds),
    [courses, recentIds, search],
  );
  const showEmpty =
    open && search.trim().length > 0 && favorites.length === 0 && available.length === 0;

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function pick(id: string) {
    onChange(id);
    setSearch('');
    setOpen(false);
  }

  if (selected) {
    return (
      <Field label={label} htmlFor="course-template" required>
        <div
          className="flex items-center gap-s3 rounded-pop border border-ds-line bg-ds-bg px-s3 py-s2"
          data-testid="course-template-selected"
        >
          <div className="min-w-0 flex-1">
            <p className={cn(TYPE.section, 'truncate text-ds-text')}>{selected.name}</p>
            {selected.detail ? (
              <p className={cn(TYPE.small, 'mt-0.5 truncate text-ds-muted')}>{selected.detail}</p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={() => {
              setSearch('');
              setOpen(false);
              onChange(null);
            }}
          >
            {changeLabel}
          </Button>
        </div>
      </Field>
    );
  }

  return (
    <Field label={label} htmlFor="course-template" required>
      <div ref={wrapperRef} className="relative" data-testid="course-template-search">
        <Input
          id="course-template"
          leadingIcon={<Search className="h-4 w-4" />}
          value={search}
          autoComplete="off"
          placeholder={placeholder}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-required="true"
          onChange={(event) => {
            setSearch(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && (favorites.length > 0 || available.length > 0 || showEmpty) ? (
          <ul
            id={listId}
            data-testid="course-template-typeahead"
            className="absolute z-10 mt-s1 max-h-60 w-full overflow-auto rounded-pop border border-ds-line bg-ds-card py-s1 shadow-pop"
          >
            <CourseGroup
              courses={favorites}
              label={recentLabel}
              testId="course-template-favorites-label"
              group="recent"
              onPick={pick}
            />
            <CourseGroup
              courses={available}
              label={availableLabel}
              testId="course-template-available-label"
              group="available"
              onPick={pick}
            />
            {showEmpty ? (
              <li>
                <p
                  className={cn(TYPE.small, 'px-s3 py-s2 text-ds-muted')}
                  data-testid="course-template-search-empty"
                >
                  {emptyLabel}
                </p>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </Field>
  );
}

function CourseGroup({
  courses,
  label,
  testId,
  group,
  onPick,
}: {
  courses: readonly CourseTemplateChoice[];
  label: string;
  testId: string;
  group: 'recent' | 'available';
  onPick: (id: string) => void;
}) {
  if (courses.length === 0) return null;
  return (
    <li className="border-b border-ds-line last:border-b-0" data-course-group={group}>
      <p className={cn(TYPE.small, 'px-s3 pb-s1 pt-s2 text-ds-muted')} data-testid={testId}>
        {label}
      </p>
      <ul>
        {courses.map((course) => (
          <CourseOption key={course.id} course={course} favorite onPick={onPick} />
        ))}
      </ul>
    </li>
  );
}

function CourseOption({
  course,
  favorite,
  onPick,
}: {
  course: CourseTemplateChoice;
  favorite: boolean;
  onPick: (id: string) => void;
}) {
  return (
    <li>
      <button
        type="button"
        data-testid="course-template-option"
        data-course-template-id={course.id}
        data-soft-favorite={favorite ? 'true' : undefined}
        onClick={() => onPick(course.id)}
        className={cn(
          'flex w-full items-center gap-s3 px-s3 py-s2 text-left hover:bg-ds-teal-tint',
          favorite && 'bg-ds-sunk',
        )}
      >
        <span className="min-w-0">
          <span className={cn(TYPE.section, 'block truncate text-ds-text')}>{course.name}</span>
          {course.detail ? (
            <span className={cn(TYPE.small, 'block truncate text-ds-muted')}>{course.detail}</span>
          ) : null}
        </span>
      </button>
    </li>
  );
}
